import { useMemo, useState } from 'react';
import { UTCTimestamp } from 'lightweight-charts';

import { ChartTypeEnum } from '../../../../../model/chart_type.enum';
import { IPortfolioPerformanceHistorical } from '../../../../../providers/portfolio-performance/portfolio-performance.types';
import { RangeListEnum } from '../../../types';
import { calendarWindow, ExtendedRange, isCalendarRange } from '../../../range';
import { PortfolioPerformanceChartType } from '../../portfolio-perfomance-chart-type';
import { useThemeMode } from '../../../../../providers/theme-provider';
import { error as errorColors } from '../../../../../theme/colors';
import { getTokenValue } from '../../../../../styles/tokens';
import Utils from '../../../../../utils/immersive_utils';

import AccountEquityHeader from './account-equity-header';

import './styles.scss';

interface AccountEquityCardProps {
  data: IPortfolioPerformanceHistorical | null | undefined;
  denomination?: string | null;
  isLoading?: boolean;
  // Initial range the chart opens on. Defaults to Y1 (live-account default);
  // backtests pass ALL so a fixed, sub-year run isn't silently truncated.
  defaultRange?: ExtendedRange;
}

const MONTH_INDEX: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

// How many trailing daily points each range shows, counting back from the
// latest available data point (not wall-clock "today" — the series can lag
// by a day, and anchoring on "now" would leave the chart empty on such days).
const RANGE_DAYS: Record<RangeListEnum, number | null> = {
  [RangeListEnum.D1]: 2,
  [RangeListEnum.D5]: 5,
  [RangeListEnum.M1]: 30,
  [RangeListEnum.M3]: 90,
  [RangeListEnum.M6]: 180,
  [RangeListEnum.Y1]: 365,
  [RangeListEnum.Y2]: 730,
  [RangeListEnum.Y3]: 1095,
  [RangeListEnum.ALL]: null,
};

// Windows a time-ordered {time, value} series to the selected range. Trailing
// ranges (1D..ALL) keep the last RANGE_DAYS points; calendar ranges (MTD/YTD)
// filter to the current month/year via the shared calendarWindow helper so the
// equity chart agrees with the indexed-comparison tab on which days are in
// period. Raw equity curve — no rebasing, just the window.
export const windowByRange = (
  points: { time: UTCTimestamp; value: number }[],
  range: ExtendedRange,
): { time: UTCTimestamp; value: number }[] => {
  if (isCalendarRange(range)) {
    const { inPeriod } = calendarWindow(range);
    return points.filter((p) => inPeriod(p.time as number));
  }
  const days = RANGE_DAYS[range];
  if (days === null) return points;
  return points.slice(-days);
};

const flattenPvSeries = (
  historical: IPortfolioPerformanceHistorical,
): { time: UTCTimestamp; value: number }[] => {
  const points: { time: UTCTimestamp; value: number }[] = [];

  for (const [year, yearData] of Object.entries(historical)) {
    for (const [month, monthData] of Object.entries(yearData.months)) {
      const monthIndex = MONTH_INDEX[month];
      if (monthIndex === undefined) continue;

      for (const [day, dayData] of Object.entries(monthData.days)) {
        if (dayData.pv === undefined || dayData.pv === null) continue;
        points.push({
          time: (Date.UTC(Number(year), monthIndex, Number(day)) / 1000) as UTCTimestamp,
          value: dayData.pv,
        });
      }
    }
  }

  return points.sort((a, b) => (a.time as number) - (b.time as number));
};

// "Underwater" / drawdown series — value is always <= 0, equity minus the
// running high-water-mark (peak PV) up to and including that day. Computed
// over the FULL chronological series, not whatever range is currently
// selected — otherwise switching the visible range would change what
// counts as "the peak so far", making historical drawdown values shift
// around depending on zoom, which isn't how a HWM is supposed to work.
export const computeUnderwaterSeries = (
  fullSeries: { time: UTCTimestamp; value: number }[],
): { time: UTCTimestamp; value: number }[] => {
  let hwm = -Infinity;
  return fullSeries.map(({ time, value }) => {
    hwm = Math.max(hwm, value);
    return { time, value: value - hwm };
  });
};

export interface HwmInfo {
  value: number;
  time: UTCTimestamp;
  // Days between the HWM and the most recent point in the series — 0 means
  // the account is at a new high today. Ties count as "still at the high"
  // (>=, not >), matching computeUnderwaterSeries's own tie-handling, where
  // a day that merely equals the prior peak also shows 0 drawdown.
  daysAgo: number;
}

// Same running-max walk as computeUnderwaterSeries, but returns the peak
// itself and when it happened rather than the distance below it — computed
// over the FULL series for the same reason (see computeUnderwaterSeries):
// this describes the same all-time HWM the chart's zero line represents,
// not whatever range happens to be selected.
export const computeHwmInfo = (
  fullSeries: { time: UTCTimestamp; value: number }[],
): HwmInfo | null => {
  if (fullSeries.length === 0) return null;

  let hwmValue = -Infinity;
  let hwmTime = fullSeries[0].time;
  for (const { time, value } of fullSeries) {
    if (value >= hwmValue) {
      hwmValue = value;
      hwmTime = time;
    }
  }

  const lastTime = fullSeries[fullSeries.length - 1].time;
  const daysAgo = Math.round(((lastTime as number) - (hwmTime as number)) / 86400);
  return { value: hwmValue, time: hwmTime, daysAgo };
};

const HWM_MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatHwmDate = (time: UTCTimestamp): string => {
  const d = new Date((time as number) * 1000);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${HWM_MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const formatDaysAgo = (daysAgo: number): string => {
  if (daysAgo <= 0) return 'today';
  if (daysAgo === 1) return '1 day ago';
  return `${daysAgo} days ago`;
};

export const AccountEquityCard = ({
  data,
  denomination,
  isLoading,
  defaultRange = RangeListEnum.Y1,
}: AccountEquityCardProps) => {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const [rangeSelected, setRangeSelected] = useState<ExtendedRange>(defaultRange);

  const series = useMemo(() => (data ? flattenPvSeries(data) : []), [data]);
  const underwaterSeries = useMemo(() => computeUnderwaterSeries(series), [series]);
  const hwmInfo = useMemo(() => computeHwmInfo(series), [series]);

  const rangeFiltered = useMemo(
    () => windowByRange(series, rangeSelected),
    [series, rangeSelected],
  );

  const underwaterRangeFiltered = useMemo(
    () => windowByRange(underwaterSeries, rangeSelected),
    [underwaterSeries, rangeSelected],
  );

  const precision = denomination === 'USD' || denomination === 'USDC' ? 2 : 4;

  return (
    <div className="account-equity-container">
      <AccountEquityHeader rangeSelected={rangeSelected} setRangeSelected={setRangeSelected} />
      <div className="chart-area">
        {rangeFiltered.length > 1 ? (
          <PortfolioPerformanceChartType
            type={ChartTypeEnum.Line}
            data={rangeFiltered}
            precision={precision}
            viewportResetKey={rangeSelected}
            showTooltip
            gridColor={isDark ? '#1f3645' : 'rgba(9,45,58,0.08)'}
            // Thicker, higher-contrast main equity line (the underwater
            // series below keeps its own error-red styling).
            lineWidth={2}
            color={getTokenValue('text.primary', resolvedMode)}
          />
        ) : (
          <div className="no-data-metrics">
            <span>{isLoading ? 'Loading equity history…' : 'No equity history yet'}</span>
          </div>
        )}
      </div>
      {rangeFiltered.length > 1 && (
        <div className="underwater-chart-area">
          <div className="underwater-chart-header">
            <span className="underwater-chart-label">Underwater (Equity − HWM)</span>
            {hwmInfo && (
              <span className="underwater-chart-hwm-info">
                HWM {Utils.formatNumber(hwmInfo.value, precision)} · {formatHwmDate(hwmInfo.time)} (
                {formatDaysAgo(hwmInfo.daysAgo)})
              </span>
            )}
          </div>
          <div className="underwater-chart-box">
            <PortfolioPerformanceChartType
              type={ChartTypeEnum.Baseline}
              data={underwaterRangeFiltered}
              color={errorColors[3]}
              precision={precision}
              viewportResetKey={rangeSelected}
              showTooltip
              gridColor={isDark ? '#1f3645' : 'rgba(9,45,58,0.08)'}
              // Underwater chart only — Katia wants no watermark here (the
              // equity chart above keeps it).
              hideWatermark
            />
          </div>
        </div>
      )}
    </div>
  );
};
